<template>
	<div class="event-color-wheel" @mouseleave="resetToOriginal">
		<svg viewBox="0 0 120 120" :style="rotationStyle">
			<circle class="wheel-0" cx="60" cy="60" r="19" @click="setOn(0)" @mouseenter="hoverOn(0)"/>
			<path class="wheel-1" @click="setOn(1)" @mouseenter="hoverOn(1)" d="M79.45,64.69l38.63,10.35c2.49-9.56,2.65-19.86,0-30.09l-38.65,10.36c.77,3.19.75,6.39.02,9.38Z"/>
			<path class="wheel-2" @click="setOn(2)" @mouseenter="hoverOn(2)" d="M102.78,17.93l-28.28,28.28c2.12,2.23,3.75,4.99,4.67,8.13l38.65-10.36c-2.82-10.18-8.11-19.02-15.04-26.06Z"/>
			<path class="wheel-3" @click="setOn(3)" @mouseenter="hoverOn(3)" d="M65.65,40.84c3.04.9,5.82,2.51,8.12,4.69l28.3-28.3c-7.27-7.13-16.25-12.32-26.06-15.04l-10.36,38.66Z"/>
			<path class="wheel-4" @click="setOn(4)" @mouseenter="hoverOn(4)" d="M55.31,40.58c3.19-.77,6.39-.75,9.38-.02L75.05,1.92c-9.56-2.49-19.86-2.65-30.09,0l10.36,38.65Z"/>
			<path class="wheel-5" @click="setOn(5)" @mouseenter="hoverOn(5)" d="M46.22,45.51c2.23-2.12,4.99-3.75,8.13-4.67L43.99,2.19c-10.18,2.82-19.02,8.11-26.06,15.04l28.28,28.28Z"/>
			<path class="wheel-6" @click="setOn(6)" @mouseenter="hoverOn(6)" d="M40.84,54.35c.9-3.04,2.51-5.82,4.69-8.12l-28.3-28.3c-7.13,7.27-12.32,16.25-15.04,26.06l38.66,10.36Z"/>
			<path class="wheel-7" @click="setOn(7)" @mouseenter="hoverOn(7)" d="M40.56,55.31L1.92,44.96c-2.49,9.56-2.65,19.86,0,30.09l38.65-10.36c-.77-3.19-.75-6.39-.02-9.38Z"/>
			<path class="wheel-8" @click="setOn(8)" @mouseenter="hoverOn(8)" d="M45.51,73.79c-2.12-2.23-3.75-4.99-4.67-8.13L2.19,76.01c2.82,10.18,8.11,19.02,15.04,26.06l28.28-28.28Z"/>
			<path class="wheel-9" @click="setOn(9)" @mouseenter="hoverOn(9)" d="M54.35,79.17c-3.04-.9-5.82-2.51-8.12-4.69l-28.3,28.3c7.27,7.13,16.25,12.32,26.06,15.04l10.36-38.66Z"/>
			<path class="wheel-10" @click="setOn(10)" @mouseenter="hoverOn(10)" d="M64.69,79.43c-3.19.77-6.39.75-9.38.02l-10.35,38.63c9.56,2.49,19.86,2.65,30.09,0l-10.36-38.65Z"/>
			<path class="wheel-11" @click="setOn(11)" @mouseenter="hoverOn(11)" d="M73.79,74.49c-2.23,2.12-4.99,3.75-8.13,4.67l10.36,38.65c10.18-2.82,19.02-8.11,26.06-15.04l-28.28-28.28Z"/>
			<path class="wheel-12" @click="setOn(12)" @mouseenter="hoverOn(12)" d="M79.17,65.65c-.9,3.04-2.51,5.82-4.69,8.12l28.3,28.3c7.13-7.27,12.32-16.25,15.04-26.06l-38.66-10.36Z"/>
			<circle class="wheel-dot" :cx="dotX" cy="60" r="4"/>
			<circle v-if="newCandidate" class="wheel-dot-original" :cx="dotXorig" cy="60" r="2"/>
		</svg>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, type CSSProperties, watch} from "vue";

const props = withDefaults(defineProps<{
	modelValue:number;
	/* If required unselected state (0 / dot in the middle) isn't allowed */
	required?:boolean;
}>(), { required: false });
const emit = defineEmits(['update:modelValue', 'clickset']);

const candidate = ref<number>(props.modelValue);
const dotX = computed<number>(() => candidate.value == 0 ? 60 : 108);
const dotXorig = computed<number>(() => props.modelValue == 0 ? 60 : 108);
const rotationStyle = computed<CSSProperties>(() => {
	const css:CSSProperties = {};
	if (candidate.value === 0) css['--rotation'] = "0deg";
	else {
		css['--rotation'] = ((candidate.value - 1) * -30) + "deg";
	}
	if (props.modelValue === 0) css['--rotation-original'] = "0deg";
	else {
		css['--rotation-original'] = ((props.modelValue - 1) * -30) + "deg";
	}
	return css;
});
const newCandidate = computed<boolean>(() => candidate.value !== props.modelValue);

function hoverOn(color:number):void {
	if (props.required && color === 0) return;	// cannot set to zero
	candidate.value = color;
}
function setOn(color:number):void {
	if (props.required && color === 0) return;	// cannot set to zero
	emit('update:modelValue', color);
	emit('clickset');
}
function resetToOriginal():void {
	candidate.value = props.modelValue;
}

watch(() => props.modelValue, (newValue: number) => candidate.value = newValue);
</script>

<style>
.event-color-wheel {
	width: 120px;
	height: 120px;
	display: inline-block;
	--rotation: 0deg;
	--rotation-original: 0deg;
}
.wheel-dot {
	fill: var(--input-fg);
	opacity: 0.5;
	transform-origin: 60px 60px;
	transform: rotate(var(--rotation));
	pointer-events: none;
}
.wheel-dot-original {
	fill: var(--input-fg);
	opacity: 0.5;
	transform-origin: 60px 60px;
	transform: rotate(var(--rotation-original));
}
.wheel-0 {
	fill: var(--input-disabled-bg);
}
.wheel-1 {
	fill: var(--red-bg);
}
.wheel-2 {
	fill: var(--orange-bg);
}
.wheel-3 {
	fill: var(--yellow-bg);
}
.wheel-4 {
	fill: var(--light-green-bg);
}
.wheel-5 {
	fill: var(--dark-green-bg);
}
.wheel-6 {
	fill: var(--green-blue-bg);
}
.wheel-7 {
	fill: var(--cyan-bg);
}
.wheel-8 {
	fill: var(--blue-bg);
}
.wheel-9 {
	fill: var(--dark-blue-bg);
}
.wheel-10 {
	fill: var(--purple-bg);
}
.wheel-11 {
	fill: var(--magenta-bg);
}
.wheel-12 {
	fill: var(--salmon-bg);
}
</style>